import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Loader2, ShieldCheck, ShieldAlert, ShieldQuestion, Search } from "lucide-react";

const inputCls =
  "px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors w-full";

export const SOURCE_LABELS = {
  npi: "NPPES NPI Registry",
  oig_leie: "OIG Exclusion List (LEIE)",
  sam_exclusions: "SAM.gov Exclusions",
};

export const ResultCard = ({ id, result }) => {
  if (!result) return null;
  const label = SOURCE_LABELS[id] || result.source;
  if (!result.checked) {
    return (
      <div className="rounded-xl border border-[#E2E8F0] p-4" data-testid={`verify-card-${id}`}>
        <div className="flex items-center gap-2 text-sm font-semibold text-[#64748B]">
          <ShieldQuestion size={16} /> {label}
        </div>
        <p className="mt-2 text-xs text-[#94A3B8]">{result.error || "Not run."}</p>
      </div>
    );
  }
  const hit = (result.match_count || 0) > 0;
  return (
    <div
      className={`rounded-xl border p-4 ${hit ? "border-[#D4AF37] bg-[#FDF8ED]" : "border-[#E2E8F0]"}`}
      data-testid={`verify-card-${id}`}
    >
      <div className={`flex items-center gap-2 text-sm font-semibold ${hit ? "text-[#8A6A12]" : "text-[#166534]"}`}>
        {hit ? <ShieldAlert size={16} /> : <ShieldCheck size={16} />} {label}
      </div>
      <p className="mt-1 text-xs text-[#64748B]">
        {hit ? `${result.match_count} potential match(es) found — review required.` : "No matches found."}
      </p>
      {hit && (
        <div className="mt-3 space-y-2">
          {(result.matches || []).slice(0, 5).map((m, i) => (
            <div key={i} className="rounded-lg bg-white border border-[#E2E8F0] p-2 text-xs text-[#0B1B2B]">
              {Object.entries(m).map(([k, v]) => (
                v ? <div key={k}><span className="text-[#94A3B8]">{k.replace(/_/g, " ")}: </span>{String(v)}</div> : null
              ))}
            </div>
          ))}
        </div>
      )}
      {result.note && <p className="mt-2 text-[11px] italic text-[#94A3B8]">{result.note}</p>}
    </div>
  );
};

export const ComplianceVerification = () => {
  const [form, setForm] = useState({ first_name: "", last_name: "", npi: "", state: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const runChecks = async () => {
    setError("");
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("First and last name are required.");
      return;
    }
    setBusy(true);
    setResults(null);
    try {
      const npi = form.npi.trim() || null;
      const state = form.state.trim() || null;
      const [npiRes, oigRes, samRes] = await Promise.all([
        api.post("/verify/npi", { first_name: form.first_name, last_name: form.last_name, npi, state }),
        api.post("/verify/oig-exclusion", { first_name: form.first_name, last_name: form.last_name, npi }),
        api.post("/verify/sam-exclusion", { first_name: form.first_name, last_name: form.last_name, npi }),
      ]);
      setResults({ npi: npiRes.data, oig_leie: oigRes.data, sam_exclusions: samRes.data });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6" data-testid="compliance-verification-panel">
      <p className="text-sm text-[#64748B] max-w-2xl">
        Screen a preceptor or clinician candidate against the free federal license and exclusion
        databases before presenting them to a university partner. These checks are informational —
        every match must be manually confirmed before any placement decision, and candidates must
        have already given written authorization for these checks to be run.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3 max-w-3xl">
        <input
          className={inputCls}
          placeholder="First name"
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          data-testid="verify-input-first-name"
        />
        <input
          className={inputCls}
          placeholder="Last name"
          value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          data-testid="verify-input-last-name"
        />
        <input
          className={inputCls}
          placeholder="NPI (optional)"
          value={form.npi}
          onChange={(e) => setForm({ ...form, npi: e.target.value })}
          data-testid="verify-input-npi"
        />
        <input
          className={inputCls}
          placeholder="State (optional)"
          value={form.state}
          onChange={(e) => setForm({ ...form, state: e.target.value })}
          data-testid="verify-input-state"
        />
      </div>

      <button
        onClick={runChecks}
        disabled={busy}
        className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0B1B2B] text-white text-sm font-semibold hover:bg-[#132A40] transition-colors disabled:opacity-60"
        data-testid="verify-run-button"
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
        Run Verification Checks
      </button>

      {error && <p className="mt-3 text-sm text-red-600" data-testid="verify-error">{error}</p>}

      {results && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="verify-results">
          <ResultCard id="npi" result={results.npi} />
          <ResultCard id="oig_leie" result={results.oig_leie} />
          <ResultCard id="sam_exclusions" result={results.sam_exclusions} />
        </div>
      )}
    </div>
  );
};
